import type React from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { CageType } from '@/domain/cages/types';
import { translateDataLabel } from '@/i18n/dataLabels';
import type { ClientLocationProps } from '@/routes/Internal/routes/Client/Client';

import { MAX_COUNT, MIN_COUNT } from './addCageForm';
import { useAddCageForm } from './useAddCageForm';

import styles from './AddCageModal.module.scss';

export interface AddCageModalProps {
    locationId?: number;
    cageTypesList?: CageType[];
    /**
     * Selectable species. Structural on purpose: only id and name are needed, so the modal
     * does not take a dependency on either the domain or the generated fish type shape.
     */
    fishTypesList?: { id: number; name: string }[];
    /** The location the pens go in; only its default species and type are read. */
    location?: Pick<ClientLocationProps, 'fishtypeid' | 'location_type'>;
    children?: React.ReactNode;
    onClose: () => void;
    createManyCages: (
        locationId: number,
        cageTypeId: number,
        fishTypeId: number,
        names: string[]
    ) => Promise<unknown>;
}

export default function AddCageModal(props: AddCageModalProps) {
    const {
        onClose,
        createManyCages,
        location,
        locationId,
        cageTypesList = [],
        fishTypesList = [],
    } = props;

    const { t } = useTranslation('clientLocations');
    const { t: tCommon } = useTranslation('common');
    // TODO: cage type names come from the DB as snake_case strings and are used as translation keys.
    // This is legacy behaviour — decoupling requires changes to CageType interfaces and mappers.
    const { t: tLegacy } = useTranslation();
    const {
        formState,
        visibleCageTypes,
        isLocked,
        hasResolvedLockedCageType,
        isResolvingLockedCageType,
        selectedCageTypeId,
        selectedFishTypeId,
        handleFishTypeChange,
        handleCageTypeChange,
        handleCountChange,
        handleNameChange,
        handleClose,
        handleSubmit,
    } = useAddCageForm({ onClose, createManyCages, location, locationId, cageTypesList });

    /* React 18's useId returns a value wrapped in colons (":r45:"), which is legal in an
       `id` attribute but not in a CSS selector, and the tests select by id. Strip the
       colons. */
    const cageTypeSelectId = useId().replace(/:/g, '');
    const fishTypeSelectId = useId().replace(/:/g, '');
    const countInputId = useId().replace(/:/g, '');

    return (
        <ModalComponent
            isOpen={locationId !== undefined}
            onClose={handleClose}
            size="lg"
            centered
            title={t('addCageModal.title')}>
            <ModalBody>
                <div className={styles.modalBody}>
                    {/* Left column: type + count */}
                    <div className={styles.settingsColumn}>
                        <div className={styles.sectionTitle}>{t('addCageModal.settings')}</div>

                        <FormField
                            label={t('addCageModal.cageType')}
                            htmlFor={cageTypeSelectId}
                            error={formState.cageTypeError}>
                            <Select
                                id={cageTypeSelectId}
                                name="cageType"
                                value={selectedCageTypeId}
                                onChange={handleCageTypeChange}
                                disabled={isLocked}
                                invalid={!!formState.cageTypeError}>
                                {!isLocked && (
                                    <option
                                        value={
                                            -1
                                        }>{`--- ${t('addCageModal.chooseCageType')} ---`}</option>
                                )}
                                {visibleCageTypes.map((cageType) => (
                                    <option key={cageType.id} value={cageType.id}>
                                        {translateDataLabel(tLegacy, 'cageTypes', cageType.name)}
                                    </option>
                                ))}
                            </Select>
                        </FormField>

                        <FormField
                            label={t('addCageModal.fishType')}
                            htmlFor={fishTypeSelectId}
                            error={formState.fishTypeError}>
                            <Select
                                id={fishTypeSelectId}
                                name="fishType"
                                value={selectedFishTypeId}
                                onChange={handleFishTypeChange}
                                invalid={!!formState.fishTypeError}>
                                <option
                                    value={
                                        -1
                                    }>{`--- ${t('addCageModal.chooseFishType')} ---`}</option>
                                {fishTypesList.map((fishType) => (
                                    <option key={fishType.id} value={fishType.id}>
                                        {translateDataLabel(tLegacy, 'fishTypes', fishType.name)}
                                    </option>
                                ))}
                            </Select>
                        </FormField>

                        <FormField
                            label={t('addCageModal.numberOfCages')}
                            htmlFor={countInputId}
                            error={formState.countError}>
                            <TextField
                                id={countInputId}
                                type="number"
                                name="count"
                                min={MIN_COUNT}
                                max={MAX_COUNT}
                                value={formState.countRaw}
                                onChange={handleCountChange}
                                invalid={!!formState.countError}
                            />
                        </FormField>
                    </div>

                    {/* Right column: name fields */}
                    <div className={styles.namesColumn}>
                        <div className={styles.sectionTitle}>{t('addCageModal.names')}</div>
                        <p className={styles.namesHint}>{t('addCageModal.namesHint')}</p>
                        <div className={styles.namesGrid}>
                            {formState.names.map((field, index) => (
                                <FormField
                                    key={index}
                                    className={styles.nameField}
                                    error={field.error}>
                                    <TextField
                                        type="text"
                                        name={`name-${index}`}
                                        placeholder={t('addCageModal.namePlaceholder')}
                                        value={field.value}
                                        onChange={(e) => handleNameChange(index, e.target.value)}
                                        invalid={!!field.error}
                                    />
                                </FormField>
                            ))}
                        </div>
                    </div>
                </div>

                {formState.systemError && <Alert tone="danger">{formState.systemError}</Alert>}
            </ModalBody>
            <ModalFooter>
                {formState.saving && (
                    <div className={styles.saving}>
                        <Spinner size="md" />
                    </div>
                )}
                {isResolvingLockedCageType && (
                    <div className={styles.resolving}>
                        <Spinner />
                        <span className={styles.resolvingText}>
                            {tLegacy('molecules.addCageModal.loading')}
                        </span>
                    </div>
                )}
                <Button variant="secondary" onClick={handleClose}>
                    {tCommon('actions.cancel')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={formState.saving || !hasResolvedLockedCageType}>
                    {tCommon('actions.add')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}
